import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FETCH_DASHBOARD } from 'actions/dashboardActions';
import { Spinner } from 'react-bootstrap';
import { 
  IconClipboardList, 
  IconCircleCheck, 
  IconCircleX, 
  IconClockHour4,
  IconChartPie,
  IconTrendingUp,
  IconListCheck,
  IconHistory,
  IconArrowRight,
  IconPointFilled,
  IconBuildingFactory2,
  IconFilter,
  IconRefresh,
  IconShieldCheck,
  IconLayersIntersect,
  IconChecklist
} from '@tabler/icons-react';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, loading } = useSelector((state) => state.dashboard);
  const [selectedStation, setSelectedStation] = useState('ALL');

  useEffect(() => {
    dispatch({ 
      type: FETCH_DASHBOARD, 
      payload: selectedStation !== 'ALL' ? { stationId: selectedStation } : {} 
    });
  }, [dispatch, selectedStation]);

  const handleRefresh = () => {
    dispatch({ 
      type: FETCH_DASHBOARD, 
      payload: selectedStation !== 'ALL' ? { stationId: selectedStation } : {} 
    });
  };

  const statusColors = {
    APPROVED: '#10b981',
    MANAGER_APPROVED: '#8b5cf6',
    QUALITY: '#0284c7',
    MANAGER: '#3b82f6',
    CONTRACTOR: '#f59e0b',
    PENDING: '#f97316',
    DRAFT: '#94a3b8',
    REWORK: '#ef4444'
  };

  /* eslint-disable react/prop-types */
  const PieChart = ({ stats }) => {
    const total = stats ? stats.reduce((acc, curr) => acc + curr.count, 0) : 0;
    let cumulativePercent = 0;

    if (!stats || total === 0) {
      return (
        <div className="text-center py-5 text-muted my-auto">
          <IconChartPie size={42} className="mb-2 opacity-50 text-primary" />
          <div className="fw-bold">No SAT status data recorded</div>
        </div>
      );
    }

    const slices = stats.filter(s => s.count > 0).map((slice) => {
      const percent = (slice.count / total) * 100;
      const startPercent = cumulativePercent;
      cumulativePercent += percent;
      return { ...slice, startPercent, percent };
    });

    return (
      <div className="d-flex flex-column align-items-center justify-content-center w-100 flex-grow-1 py-2">
        {/* Perfectly Proportioned 200px Donut Chart */}
        <div 
          className="pie-container my-3" 
          style={{ 
            width: '200px', 
            height: '200px', 
            position: 'relative', 
            flexShrink: 0,
            filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.06))' 
          }}
        >
          <svg viewBox="0 0 32 32" style={{ transform: 'rotate(-90deg)', borderRadius: '50%', background: '#f8fafc', width: '100%', height: '100%' }}>
            {slices.map((slice, i) => {
              const dashArray = `${slice.percent} ${100 - slice.percent}`;
              const dashOffset = 100 - slice.startPercent;
              return (
                <circle
                  key={i}
                  r="16"
                  cx="16"
                  cy="16"
                  fill="transparent"
                  stroke={statusColors[slice.status] || '#cbd5e0'}
                  strokeWidth="32"
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                  style={{ transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
              );
            })}
            <circle r="10.5" cx="16" cy="16" fill="white" />
          </svg>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
            <span style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', display: 'block', lineHeight: 1 }}>{total}</span>
            <span className="badge bg-primary-lt mt-1 text-uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
              SAT Forms
            </span>
          </div>
        </div>

        {/* Spacious Legend Breakdown Grid */}
        <div className="w-100 mt-3 pt-3 border-top">
          <div className="row g-2">
            {stats.filter(s => s.count > 0).map((slice, i) => {
              const pct = Math.round((slice.count / total) * 100);
              return (
                <div key={i} className="col-12 col-sm-6">
                  <div className="p-2 px-3 rounded-3 border bg-white d-flex align-items-center justify-content-between shadow-xs">
                    <div className="d-flex align-items-center gap-2">
                      <IconPointFilled size={16} style={{ color: statusColors[slice.status] || '#cbd5e0' }} />
                      <span className="fw-bold text-dark" style={{ fontSize: '12px' }}>
                        {slice.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">{pct}%</span>
                      <span className="badge bg-light text-dark fw-bold" style={{ fontSize: '12px' }}>
                        {slice.count}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="page-body py-3">
      {loading && !data ? (
        <div className="loader-container">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="container-xl premium-dashboard-stats">
          {/* Spacious Header & Controls Toolbar */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 p-3 px-4 glass-panel">
            <div className="d-flex align-items-center gap-3">
              <span className="badge bg-primary-lt p-2 rounded-3">
                <IconChecklist size={22} className="text-primary" />
              </span>
              <div>
                <h3 className="m-0 fw-bold" style={{ fontSize: '1.35rem', color: '#0f172a' }}>
                  SAT Forms Analytics Hub
                </h3>
                <p className="text-muted small m-0 mt-0.5">
                  Real-time monitoring across all 12 Site Acceptance Testing (SAT) categories
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              {/* Station Filter Dropdown */}
              <div className="d-flex align-items-center gap-2 bg-white px-3 py-1.5 rounded-3 border" style={{ minWidth: '210px' }}>
                <IconFilter size={18} className="text-muted" />
                <select 
                  className="form-select form-select-sm border-0 p-0 shadow-none fw-bold" 
                  value={selectedStation}
                  onChange={(e) => setSelectedStation(e.target.value)}
                  style={{ cursor: 'pointer', background: 'transparent', fontSize: '13px' }}
                >
                  <option value="ALL">All Stations ({data?.stationsList?.length || 0})</option>
                  {data?.stationsList?.map((stId, idx) => (
                    <option key={idx} value={stId}>{stId}</option>
                  ))}
                </select>
              </div>

              <button 
                className="btn btn-outline-primary btn-icon rounded-3" 
                onClick={handleRefresh}
                title="Refresh Dashboard Data"
              >
                <IconRefresh size={18} />
              </button>

              <span className="badge bg-blue-lt px-3 py-2 d-none d-md-inline-flex align-items-center gap-1.5" style={{ fontSize: '12px' }}>
                <IconHistory size={15} /> Updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>

          {/* 6 Key Stats Cards */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <div className="stat-card glass-panel primary-gradient">
                <div className="stat-icon-wrapper"><IconClipboardList size={22} /></div>
                <div className="stat-details">
                  <span className="stat-label">Total SAT Forms</span>
                  <h4 className="stat-value">{data?.totalCount || 0}</h4>
                  <div className="stat-footer">
                    <span className="trend positive"><IconTrendingUp size={14} /> Global</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <div className="stat-card glass-panel success-gradient">
                <div className="stat-icon-wrapper"><IconCircleCheck size={22} /></div>
                <div className="stat-details">
                  <span className="stat-label">Fully Approved</span>
                  <h4 className="stat-value">{data?.totalCountApproved || 0}</h4>
                  <div className="stat-footer">
                    <div className="progress-mini">
                      <div 
                        className="progress-bar-fill" 
                        style={{ width: `${data?.totalCount ? (data.totalCountApproved / data.totalCount) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="progress-label">{data?.totalCount ? Math.round((data.totalCountApproved / data.totalCount) * 100) : 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <div className="stat-card glass-panel warning-gradient">
                <div className="stat-icon-wrapper"><IconClockHour4 size={22} /></div>
                <div className="stat-details">
                  <span className="stat-label">In Review</span>
                  <h4 className="stat-value">{data?.totalCountInProgress || 0}</h4>
                  <div className="stat-footer">
                    <div className="progress-mini">
                      <div 
                        className="progress-bar-fill" 
                        style={{ width: `${data?.totalCount ? (data.totalCountInProgress / data.totalCount) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="progress-label">{data?.totalCount ? Math.round((data.totalCountInProgress / data.totalCount) * 100) : 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <div className="stat-card glass-panel danger-gradient">
                <div className="stat-icon-wrapper"><IconCircleX size={22} /></div>
                <div className="stat-details">
                  <span className="stat-label">Rework Needed</span>
                  <h4 className="stat-value">{data?.totalCountReject || 0}</h4>
                  <div className="stat-footer">
                    <div className="progress-mini">
                      <div 
                        className="progress-bar-fill" 
                        style={{ width: `${data?.totalCount ? (data.totalCountReject / data.totalCount) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="progress-label">{data?.totalCount ? Math.round((data.totalCountReject / data.totalCount) * 100) : 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <div className="stat-card glass-panel info-gradient" style={{ background: 'rgba(255,255,255,0.85)' }}>
                <div className="stat-icon-wrapper" style={{ background: 'rgba(114, 9, 183, 0.1)', color: '#7209b7' }}>
                  <IconBuildingFactory2 size={22} />
                </div>
                <div className="stat-details">
                  <span className="stat-label">Active Stations</span>
                  <h4 className="stat-value" style={{ color: '#7209b7' }}>{data?.totalStations || 0}</h4>
                  <div className="stat-footer">
                    <span className="text-muted fw-bold" style={{ fontSize: '11px' }}>Monitored</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <div className="stat-card glass-panel emerald-gradient" style={{ background: 'rgba(255,255,255,0.85)' }}>
                <div className="stat-icon-wrapper" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  <IconShieldCheck size={22} />
                </div>
                <div className="stat-details">
                  <span className="stat-label">Compliance Rate</span>
                  <h4 className="stat-value" style={{ color: '#10b981' }}>{data?.completionRate || 0}%</h4>
                  <div className="stat-footer">
                    <span className="trend positive fw-bold">Overall Pass</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Level 1 & Level 2 Stage Verification Summary */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-6">
              <div className="glass-panel p-3 px-4 h-100 border-start border-4 border-info">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2.5">
                    <IconLayersIntersect size={20} className="text-info" />
                    <h5 className="m-0 fw-bold" style={{ fontSize: '14px', color: '#0f172a' }}>Level 1 (L1) Initial Verification</h5>
                  </div>
                  <div className="d-flex align-items-center gap-4">
                    <div>
                      <span className="text-muted small me-1.5">Approved:</span>
                      <span className="fw-extrabold text-success" style={{ fontSize: '16px' }}>{data?.levelWise?.l1Completed || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted small me-1.5">Pending:</span>
                      <span className="fw-extrabold text-warning" style={{ fontSize: '16px' }}>{data?.levelWise?.l1Pending || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="glass-panel p-3 px-4 h-100 border-start border-4" style={{ borderColor: '#8b5cf6' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2.5">
                    <IconShieldCheck size={20} style={{ color: '#8b5cf6' }} />
                    <h5 className="m-0 fw-bold" style={{ fontSize: '14px', color: '#0f172a' }}>Level 2 (L2) Operational Readiness</h5>
                  </div>
                  <div className="d-flex align-items-center gap-4">
                    <div>
                      <span className="text-muted small me-1.5">Approved:</span>
                      <span className="fw-extrabold text-success" style={{ fontSize: '16px' }}>{data?.levelWise?.l2Completed || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted small me-1.5">Pending:</span>
                      <span className="fw-extrabold text-warning" style={{ fontSize: '16px' }}>{data?.levelWise?.l2Pending || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SAT Form Types Grid (2 Columns) & Status Pie Chart */}
          <div className="row g-4 mb-4">
            {/* 12 SAT Form Types Distribution (2 Columns) */}
            <div className="col-12 col-xl-7">
              <div className="glass-panel p-4 h-100">
                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <IconListCheck size={20} className="text-primary" />
                    <h5 className="m-0 fw-bold" style={{ fontSize: '15px', color: '#0f172a' }}>12 SAT Checklist Categories</h5>
                  </div>
                  <span className="badge bg-light text-muted">All Form Models</span>
                </div>

                <div className="row g-3">
                  {data?.formTypes?.map((type, i) => {
                    const pct = data.totalCount > 0 ? Math.round((type.count / data.totalCount) * 100) : 0;
                    return (
                      <div key={i} className="col-12 col-md-6">
                        <div className="p-3 rounded-3 border bg-white shadow-xs">
                          <div className="d-flex align-items-center justify-content-between mb-1.5">
                            <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>{type.name}</span>
                            <span className="badge bg-primary-lt fw-extrabold" style={{ fontSize: '12px' }}>{type.count}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2 text-muted small mb-2" style={{ fontSize: '11px' }}>
                            <span className="text-success fw-bold">✓ {type.approved || 0} Appr</span>
                            <span>•</span>
                            <span className="text-warning fw-bold">⏳ {type.inProgress || 0} Prog</span>
                            <span>•</span>
                            <span className="text-danger fw-bold">✖ {type.rework || 0} Rewk</span>
                          </div>
                          <div className="progress-mini" style={{ height: '6px', background: '#f1f5f9' }}>
                            <div 
                              className="progress-bar-fill" 
                              style={{ 
                                width: `${pct}%`,
                                background: 'linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)'
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!data?.formTypes || data.formTypes.length === 0) && (
                    <div className="text-muted text-center py-4 small">No SAT form records found</div>
                  )}
                </div>
              </div>
            </div>

            {/* Status Breakdown Donut Chart */}
            <div className="col-12 col-xl-5">
              <div className="glass-panel p-4 h-100 d-flex flex-column justify-content-between">
                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <IconChartPie size={20} className="text-primary" />
                    <h5 className="m-0 fw-bold" style={{ fontSize: '15px', color: '#0f172a' }}>SAT Workflow Status Composition</h5>
                  </div>
                </div>
                <PieChart stats={data?.statusWise || []} />
              </div>
            </div>
          </div>

          {/* Top Stations Compliance Leaderboard & Recent Activity */}
          <div className="row g-4">
            {/* Top Stations Leaderboard */}
            <div className="col-12 col-xl-5">
              <div className="glass-panel p-4 h-100">
                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <IconBuildingFactory2 size={20} className="text-primary" />
                    <h5 className="m-0 fw-bold" style={{ fontSize: '15px', color: '#0f172a' }}>Station SAT Progress</h5>
                  </div>
                  <span className="text-muted small">Top Sites</span>
                </div>

                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>Station ID</th>
                        <th>Forms</th>
                        <th>Status</th>
                        <th>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.stationWise?.map((st, i) => (
                        <tr 
                          key={i} 
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/formList/${st.stationId}`)}
                        >
                          <td className="py-2.5">
                            <div className="fw-bold text-dark" style={{ fontSize: '13px' }}>{st.stationId}</div>
                            <div className="text-muted small">{st.projectName}</div>
                          </td>
                          <td className="fw-bold py-2.5">{st.totalForms}</td>
                          <td className="py-2.5">
                            <span className="badge bg-success-lt me-1">{st.approvedForms} ✓</span>
                            <span className="badge bg-warning-lt">{st.inProgressForms} ⏳</span>
                          </td>
                          <td className="py-2.5" style={{ width: '100px' }}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress-mini flex-grow-1" style={{ height: '6px', background: '#e2e8f0' }}>
                                <div 
                                  className="progress-bar-fill" 
                                  style={{ 
                                    width: `${st.completionRate}%`,
                                    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                                  }}
                                ></div>
                              </div>
                              <span className="small fw-bold text-muted">{st.completionRate}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!data?.stationWise || data.stationWise.length === 0) && (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-muted small">No station activity recorded</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Latest SAT Activity Stream */}
            <div className="col-12 col-xl-7">
              <div className="glass-panel p-4 h-100">
                <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <IconHistory size={20} className="text-primary" />
                    <h5 className="m-0 fw-bold" style={{ fontSize: '15px', color: '#0f172a' }}>Recent SAT Submissions</h5>
                  </div>
                  <span className="text-muted small">Latest Logs</span>
                </div>

                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>Form Type</th>
                        <th>Station</th>
                        <th>L1</th>
                        <th>L2</th>
                        <th>Overall Status</th>
                        <th>Updated</th>
                        <th className="w-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.latestUpdates?.map((update, i) => (
                        <tr 
                          key={i}
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/formList/${update.stationId}`)}
                        >
                          <td className="py-2.5">
                            <div className="d-flex align-items-center">
                              <span className="badge bg-blue-lt me-2 fw-bold">{update.formName.charAt(0)}</span>
                              <span className="fw-bold text-dark">{update.formName}</span>
                            </div>
                          </td>
                          <td className="text-muted font-weight-medium py-2.5">{update.stationId}</td>
                          <td className="py-2.5">
                            <span className="badge bg-light text-secondary font-weight-bold">
                              {update.l1Status}
                            </span>
                          </td>
                          <td className="py-2.5">
                            <span className="badge bg-light text-secondary font-weight-bold">
                              {update.l2Status}
                            </span>
                          </td>
                          <td className="py-2.5">
                            <span className={`badge bg-${
                              (update.status || '').toLowerCase() === 'approved' ? 'success' : 
                              (update.status || '').toLowerCase() === 'manager_approved' ? 'primary' :
                              (update.status || '').toLowerCase() === 'rework' ? 'danger' : 
                              (update.status || '').toLowerCase() === 'draft' ? 'secondary' : 'warning'
                            }-lt px-2 py-1`}>
                              {update.status}
                            </span>
                          </td>
                          <td className="text-muted small py-2.5">{new Date(update.updatedAt).toLocaleDateString()}</td>
                          <td className="py-2.5">
                            <IconArrowRight size={16} className="text-primary" />
                          </td>
                        </tr>
                      ))}
                      {(!data?.latestUpdates || data.latestUpdates.length === 0) && (
                        <tr>
                          <td colSpan="7" className="text-center py-4 text-muted small">No recent updates recorded</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default Dashboard;
